// src/data/salesOnboardingData.js
// Enablement Playbooks — Sales Onboarding Framework (ACME, Inc.)
// VERSION: Executive Polish

export const salesOnboardingData = {
  // =========================
  // Block 1: Meta Header
  // =========================
  meta: {
    title: "Sales Onboarding Framework",
    // UPGRADE 1: High-impact End-State definition
    subtitle:
      "End State: Every seller globally earns autonomy only after demonstrating certified selling competency—measured, coached, and inspected.",
    outcomes: [
      "Reduced time-to-first-meeting",
      "Accelerated time-to-pipeline",
      "Higher-quality discovery and qualification",
    ],
  },

  // =========================
  // Block 2: Strategy Tabs
  // =========================
  strategy: {
    heading: "Overarching Strategy",
    tabs: {
      executive: {
        body: "ACME’s Sales Onboarding Framework turns ramp into a certified pathway—not a calendar event. New hires progress through a role-based journey that standardizes what “good” looks like across regions and motions. The program is designed to validate observable behaviors: insight-led discovery, disciplined qualification, confident messaging, and tool hygiene that supports pipeline inspection. Content is sequenced to reduce overload and reinforce learning through practice, coaching, and real scenarios. Certification is earned through demonstrated capability—role plays, call reviews, and stage-based exit criteria—so sellers earn autonomy only when they can execute ACME’s sales motion with consistency.",
        examplesHeading: "Examples",
        examples: [
          "New AEs must demonstrate an executive discovery flow and complete a qualification scorecard on a simulated ACME opportunity before advancing to Phase Four.",
          "SDRs certify geo-specific outbound messaging and meeting-setting readiness before launching live sequences.",
        ],
      },

      sages: {
        intro:
          "Our onboarding system is anchored in proven selling and enablement methodologies. These ‘Sages’ are the logic behind the sequence—so execution stays consistent, coachable, and measurable.",
        sages: [
          {
            name: "Mark Roberge",
            philosophy: "The Science of Scaling",
            bullets: [
              "Define seller competencies before scaling headcount.",
              "Measure readiness through demonstrated behaviors—not tenure.",
              "Standardize coaching so a rep in EMEA ramps like a rep in NOAM.",
            ],
          },
          {
            name: "The Challenger Sale",
            philosophy: "Lead With Insight",
            bullets: [
              "Train reps to teach a point of view, not recite features.",
              "Link ACME research to executive priorities and urgency.",
              "Build confidence to control the conversation and next steps.",
            ],
          },
          {
            name: "MEDDICC",
            philosophy: "Qualification Discipline",
            bullets: [
              "Bake deal rigor into onboarding, not after ramp.",
              "Use stage-based exit criteria to prevent weak pipeline.",
              "Create a common language for inspection and forecasting.",
            ],
          },
          {
            name: "Jill Konrath",
            philosophy: "Speed to Productivity",
            bullets: [
              "Compress ramp with short modules + immediate practice.",
              "Prioritize what must be true by Day 30, 60, 90.",
              "Remove ‘nice-to-know’ content that slows the field.",
            ],
          },
        ],
      },

      deliverables: {
        items: [
          // UPGRADE 2: Certification Snapshot (Formatted as list items)
          "CERTIFICATION LEVEL 1: Foundations (Knowledge Check)",
          "CERTIFICATION LEVEL 2: Role Application (Simulated Scenarios)",
          "CERTIFICATION LEVEL 3: Live Readiness (Field Execution)",
          "------------------------------------------------",
          "Role-based competency maps (SDR, AE, Specialist, Manager)",
          "Discovery rubric + call review scoring guide",
          "Qualification scorecard aligned to ACME stages",
          "Geo-specific messaging plays and persona talk tracks",
          "Tool and CRM workflow standards (hygiene + inspection readiness)",
          "Onboarding content taxonomy (role, region, motion, stage)",
        ],
      },
    },
  },

  // =========================
  // Block 3: Timeline
  // =========================
  timeline: {
    heading: "Phases & Timeline",
    phases: [
      {
        phaseName: "Phase One: Transfer + Normalize",
        duration: "Days 1–14",
        purpose:
          "Transfer existing onboarding content into the LMS and establish a clean baseline.",
        deliverablesHeading: "Key Deliverables",
        deliverables: [
          "Existing sales/onboarding assets transferred into LMS",
          "Content inventory and archive list (remove duplicates/outdated items)",
          "ACME sales motion overview: stages, handoffs, and expectations",
          "Role definitions: SDR, AE, Specialist, Manager (what success means)",
        ],
      },
      {
        phaseName: "Phase Two: Geo Context + Role Learning Paths",
        duration: "Days 15–45",
        purpose:
          "Insert geo-specific content and publish role-based learning paths aligned to how ACME sells globally.",
        deliverablesHeading: "Key Deliverables",
        deliverables: [
          "Role learning paths published (SDR/AE/Specialist/Manager)",
          "Geo-specific buyer context (NOAM/EMEA/APAC) + market nuance",
          "Core vs. Growth motion differences documented and trained",
          "Persona messaging guide (CFO/CIO/VP Ops/Strategy) and talk tracks",
          "Objection handling workshop + messaging library",
        ],
      },
      {
        phaseName: "Phase Three: Asynchronous Mastery",
        duration: "Days 46–60",
        purpose:
          "Add asynchronous eLearning to build foundational product and selling knowledge without slowing the field.",
        deliverablesHeading: "Key Deliverables",
        deliverables: [
          "Modular eLearning: ACME narrative + research products + differentiation",
          "Asynchronous discovery training with practice prompts",
          "Knowledge checks + applied scenario assessments",
          "Tool enablement micro-learnings (CRM workflow, enablement hub usage)",
        ],
      },
      {
        phaseName:
          "Phase Four: Certified Readiness (Role-Based New Hire Program)",
        duration: "Days 61–90",
        purpose:
          "Launch the comprehensive role-based new hire program with certification, coaching, and sign-off.",
        deliverablesHeading: "Key Deliverables",
        deliverables: [
          "Certification events: live role-plays, call reviews, scenario defenses",
          "Manager coaching cadence: weekly inspection + feedback loop",
          "Final readiness rubric + approval workflow",
          "Handoff standards practiced (SDR→AE, AE→CS) with quality criteria",
        ],
        subSections: [
          {
            title: "Foundational Product Training",
            bullets: [
              "ACME value narrative: what we sell, why it matters, and who it’s for",
              "Use-case mapping: buyer problem → ACME insight → outcome proof",
              "Competitive differentiation: how ACME wins vs Forrester-style rivals",
            ],
          },
          {
            title: "Sales Method & Process Training",
            bullets: [
              "Insight-led discovery flow and executive meeting structure",
              "Qualification discipline: scorecards, exit criteria, next-step control",
              "Deal progression: stakeholder mapping and consensus building",
            ],
          },
          {
            title: "Systems & Tools Training",
            bullets: [
              "CRM hygiene by stage (required fields + inspection standards)",
              "Enablement hub navigation (plays, decks, messaging, assets)",
              "Call recording workflow (submit, review, iterate, certify)",
            ],
          },
          {
            title: "Alignment by Roles",
            bullets: [
              "SDR: outbound, qualification, meeting quality, handoff readiness",
              "AE: discovery, multi-threading, progression, forecast discipline",
              "Specialist: demo excellence, value proof, competitive clarity",
              "Manager: coaching, inspection, certification governance",
            ],
          },
        ],
      },
    ],
  },

  // =========================
  // Block 4: Resources
  // =========================
  resources: {
    heading: "Resources Needed",
    minimumViableTeam: {
      title: "Minimum Viable Team",
      items: [
        "Sales Enablement Lead (Program Owner)",
        "Sales Leadership Sponsor (Standards + methodology ownership)",
        "Product/Research SME (narrative + differentiation)",
        "LMS / Enablement Platform Admin",
      ],
    },
    idealTeam: {
      title: "Ideal Team",
      items: [
        "Instructional Designer (modules + assessments + sequencing)",
        "Regional Sales Leaders (geo relevance + adoption)",
        "Sales Operations Analyst (CRM workflow + inspection alignment)",
        "Coaching Lead / Call Review Captain (quality + certification rigor)",
      ],
    },
  },

  // =========================
  // Block 5: Risks
  // =========================
  risks: {
    heading: "Risks & Mitigation",
    rows: [
      {
        // UPGRADE 3: Revenue Tie-Back
        risk: "Certification viewed as 'checking the box'",
        mitigation:
          "Certification gates are tied to pipeline quality signals—not course completion.",
      },
      {
        risk: "Onboarding becomes content-heavy and slows ramp",
        mitigation:
          "Sequence by role; prioritize Day 30/60/90 outcomes; modularize learning.",
      },
      {
        risk: "Managers coach inconsistently across regions",
        mitigation:
          "Standardized rubrics + required certification events + sign-off workflow.",
      },
      {
        risk: "Geo training doesn’t reflect local buyers",
        mitigation:
          "Lock global core; add regional extensions maintained by geo leads.",
      },
      {
        risk: "Weak qualification creates noisy pipeline",
        mitigation:
          "Embed MEDDICC-style scorecards and stage exit criteria early.",
      },
      {
        risk: "CRM hygiene undermines inspection and forecasting",
        mitigation:
          "Teach stage-based workflows; enforce required fields; coach to inspection standards.",
      },
      {
        risk: "Content decays as ACME narrative evolves",
        mitigation:
          "Quarterly governance reviews; versioned play updates and ownership.",
      },
    ],
  },
};
